import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { User, ArrowRight, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

export function IdentificationStep({ dni, setDni, birthDay, setBirthDay, birthMonth, setBirthMonth, birthYear, setBirthYear, submitting, onSubmit, enableVoterTracking, enableVotMaAlcada, enableAudit }) {
  const { t } = useTranslation();

  return (
    <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="bg-white border border-slate-200 rounded-xl p-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center"><User className="h-6 w-6 text-blue-600" /></div>
        <div>
          <h3 className="text-xl font-heading font-semibold text-slate-900">{t('voting.identification')}</h3>
          <p className="text-sm text-slate-500">{t('voting.enterDataToVerify')}</p>
        </div>
      </div>

      {enableVotMaAlcada && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg p-4 mb-6" data-testid="hand-vote-warning">
          <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">{t('voting.notAnonymousHandVote')}</p>
        </div>
      )}

      {!enableVotMaAlcada && enableAudit && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6" data-testid="audit-warning">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">{t('voting.auditWarning')}</p>
        </div>
      )}

      {!enableVotMaAlcada && !enableAudit && enableVoterTracking && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6" data-testid="voter-tracking-warning">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">{t('voting.notAnonymousTracking')}</p>
        </div>
      )}
      <form onSubmit={onSubmit}>
        <div className="form-group mb-4">
          <Label htmlFor="dni" className="form-label">{t('voting.dniLabel')}</Label>
          <Input id="dni" value={dni} onChange={(e) => setDni(e.target.value.toUpperCase())} placeholder="12345678A" className="h-12 text-lg" data-testid="dni-input" />
        </div>
        <div className="form-group mb-6">
          <Label className="form-label">{t('voting.birthDate')}</Label>
          <div className="flex gap-2 items-center">
            <Input inputMode="numeric" pattern="[0-9]*" maxLength={2} placeholder="DD" value={birthDay}
              onChange={(e) => { const v = e.target.value.replace(/\D/g, '').slice(0, 2); setBirthDay(v); if (v.length === 2) e.target.nextElementSibling?.nextElementSibling?.focus(); }}
              className="h-12 text-lg text-center w-16" data-testid="birth-day-input" />
            <span className="text-slate-400 text-lg">/</span>
            <Input inputMode="numeric" pattern="[0-9]*" maxLength={2} placeholder="MM" value={birthMonth}
              onChange={(e) => { const v = e.target.value.replace(/\D/g, '').slice(0, 2); setBirthMonth(v); if (v.length === 2) e.target.nextElementSibling?.nextElementSibling?.focus(); }}
              className="h-12 text-lg text-center w-16" data-testid="birth-month-input" />
            <span className="text-slate-400 text-lg">/</span>
            <Input inputMode="numeric" pattern="[0-9]*" maxLength={4} placeholder="AAAA" value={birthYear}
              onChange={(e) => setBirthYear(e.target.value.replace(/\D/g, '').slice(0, 4))}
              className="h-12 text-lg text-center w-24" data-testid="birth-year-input" />
          </div>
        </div>
        <Button type="submit" className="w-full h-12 bg-slate-900 hover:bg-slate-800" disabled={submitting} data-testid="verify-dni-btn">
          {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <>{t('voting.continue')}<ArrowRight className="h-5 w-5 ml-2" /></>}
        </Button>
      </form>
    </motion.div>
  );
}
